import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { Heart, Sparkles, Baby, ArrowRight, MapPin, X, Star, Users, Brain, Briefcase, HelpCircle, MessageCircle, Calendar } from "lucide-react";
import { 
  PHASES, 
  WORKING_STATUS_OPTIONS, 
  HELP_TOPICS, 
  DEFAULT_INTERESTS,
  PHASES_WITH_CHILDREN_INFO,
  getChildrenInfoOptionsForPhase,
  getChildrenInfoLabel
} from "@/constants/profileOptions";

// Icon mapping for dynamic phases
const ICON_MAP = {
  Sparkles: Sparkles,
  Heart: Heart,
  Baby: Baby,
  Star: Star,
  Users: Users,
  Brain: Brain
};

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [phases, setPhases] = useState(PHASES);
  const [interests, setInterests] = useState(DEFAULT_INTERESTS);
  const [formData, setFormData] = useState({
    phase: user?.phase || "",
    bio: user?.bio || "",
    location: user?.location || "",
    interests: user?.interests || [],
    working_status: user?.working_status || "",
    children_info: user?.children_info || "",
    looking_for_help: user?.looking_for_help || [],
    willing_to_help: user?.willing_to_help || [],
    due_date: user?.due_date || "",
    child_birthday: user?.child_birthday || ""
  });

  // Fetch phases and interests from API
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [phasesRes, interestsRes] = await Promise.all([
          axios.get(`${API}/config/phases`),
          axios.get(`${API}/config/interests`)
        ]);
        if (phasesRes.data.length > 0) setPhases(phasesRes.data);
        if (interestsRes.data.length > 0) setInterests(interestsRes.data.map(i => i.label || i.value));
      } catch (error) {
        console.error("Failed to fetch config:", error);
        // Keep defaults on error
      }
    };
    fetchConfig();
  }, []);

  const handlePhaseSelect = (phaseId) => {
    setFormData(prev => ({ ...prev, phase: phaseId }));
  };

  const handleInterestToggle = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleNext = () => {
    if (step === 1 && !formData.phase) {
      toast.error("Please select your phase");
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.put(
        `${API}/users/me`,
        {
          ...formData,
          profile_completed: true
        },
        { withCredentials: true }
      );
      
      updateUser(response.data);
      toast.success("Profile completed! Welcome to WOMBS");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      console.error("Profile update error:", error);
      toast.error("Failed to save profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute top-1/4 left-0 w-96 h-96 bg-sage/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-clay/10 rounded-full blur-3xl" />

      {/* Header */}
      <header className="relative z-10 px-6 py-6">
        <img 
          src="https://customer-assets.emergentagent.com/job_68779cce-c06c-4c5e-9076-4d6253f6c7a5/artifacts/j5uq6m0s_Artboard%204_4%403x.png" 
          alt="WOMBS" 
          className="h-10"
        />
      </header>

      {/* Progress */}
      <div className="relative z-10 px-6 max-w-2xl mx-auto">
        <div className="flex gap-2 mb-8">
          {[1, 2, 3, 4, 5].map((s) => (
            <div 
              key={s}
              className={`h-1 flex-1 rounded-full transition-all duration-500 ${
                s <= step ? 'bg-sage' : 'bg-sage/20'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="relative z-10 px-6 pb-20 max-w-2xl mx-auto">
        {/* Step 1: Phase Selection */}
        {step === 1 && (
          <div className="space-y-8 animate-fadeIn" data-testid="onboarding-step-1">
            <div className="text-center space-y-2">
              <h1 className="text-3xl md:text-4xl font-serif text-sage-darker">
                Welcome, {user?.name?.split(' ')[0]}
              </h1>
              <p className="text-muted-foreground">
                Where are you in your maternal journey?
              </p>
            </div>

            <div className="space-y-4">
              {phases.map((phase) => {
                const Icon = ICON_MAP[phase.icon] || Sparkles;
                const isSelected = formData.phase === phase.value;
                return (
                  <button
                    key={phase.value}
                    onClick={() => handlePhaseSelect(phase.value)}
                    className={`w-full p-6 rounded-2xl border transition-all duration-300 text-left ${
                      isSelected
                        ? 'border-sage bg-sage/10 shadow-md'
                        : 'border-sage/20 bg-white hover:border-sage/40'
                    }`}
                    data-testid={`phase-${phase.value}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        phase.color === 'sage' ? 'bg-sage/20' :
                        phase.color === 'clay' ? 'bg-clay/20' : 'bg-purple-100'
                      }`}>
                        <Icon className={`w-6 h-6 ${
                          phase.color === 'sage' ? 'text-sage-dark' :
                          phase.color === 'clay' ? 'text-clay-dark' : 'text-purple-600'
                        }`} strokeWidth={1.5} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-sage-darker">{phase.label}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{phase.description}</p>
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? 'border-sage bg-sage' : 'border-sage/30'
                      }`}>
                        {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Due Date for Pregnant */}
            {formData.phase === 'pregnant' && (
              <div className="space-y-2 p-4 bg-white rounded-xl border border-sage/20">
                <label className="text-sm text-sage-darker font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> When is your due date?
                </label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                  className="bg-cream border-sage/20"
                />
              </div>
            )}

            {/* Child Birthday for Postpartum */}
            {formData.phase === 'postpartum' && (
              <div className="space-y-2 p-4 bg-white rounded-xl border border-sage/20">
                <label className="text-sm text-sage-darker font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> When was your baby born?
                </label>
                <Input
                  type="date"
                  value={formData.child_birthday}
                  onChange={(e) => setFormData(prev => ({ ...prev, child_birthday: e.target.value }))}
                  className="bg-cream border-sage/20"
                />
              </div>
            )}

            <Button
              onClick={handleNext}
              disabled={!formData.phase}
              className="w-full bg-sage text-white hover:bg-sage-dark rounded-full py-6 font-medium"
              data-testid="next-step-btn"
            >
              Continue
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Bio, Location, Work & Family */}
        {step === 2 && (
          <div className="space-y-8 animate-fadeIn" data-testid="onboarding-step-2">
            <div className="text-center space-y-2">
              <h1 className="text-3xl md:text-4xl font-serif text-sage-darker">
                Tell us about yourself
              </h1>
              <p className="text-muted-foreground">
                Help others in the community get to know you
              </p>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm text-sage-darker font-medium">Bio</label>
                <Textarea
                  placeholder="Share a little about yourself, your journey, or what brings you here..."
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                  className="min-h-[100px] bg-white border-sage/20 text-foreground placeholder:text-muted-foreground focus:border-sage resize-none"
                  data-testid="bio-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-sage-darker font-medium">Location</label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    placeholder="City, Country"
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    className="pl-12 bg-white border-sage/20 text-foreground placeholder:text-muted-foreground focus:border-sage"
                    data-testid="location-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-sage-darker font-medium flex items-center gap-2">
                  <Briefcase className="w-4 h-4" /> Work Status
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {WORKING_STATUS_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setFormData(prev => ({ ...prev, working_status: option.value }))}
                      className={`p-3 rounded-xl border text-sm text-left transition-all ${
                        formData.working_status === option.value
                          ? 'border-sage bg-sage/10 text-sage-dark'
                          : 'border-sage/20 bg-white text-muted-foreground hover:border-sage/40'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Children Info - Only show for relevant phases */}
              {PHASES_WITH_CHILDREN_INFO.includes(formData.phase) && getChildrenInfoOptionsForPhase(formData.phase).length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm text-sage-darker font-medium flex items-center gap-2">
                    <Baby className="w-4 h-4" /> 
                    {formData.phase === 'ttc' ? 'Your Situation' : 'Children'}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {getChildrenInfoOptionsForPhase(formData.phase).map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setFormData(prev => ({ ...prev, children_info: option.value }))}
                        className={`p-3 rounded-xl border text-sm text-left transition-all ${
                          formData.children_info === option.value
                            ? 'border-sage bg-sage/10 text-sage-dark'
                            : 'border-sage/20 bg-white text-muted-foreground hover:border-sage/40'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={handleBack}
                className="flex-1 border-sage/30 text-sage-darker hover:bg-sage/10 rounded-full py-6"
                data-testid="back-btn"
              >
                Back
              </Button>
              <Button
                onClick={handleNext}
                className="flex-1 bg-sage text-white hover:bg-sage-dark rounded-full py-6 font-medium"
                data-testid="next-step-btn"
              >
                Continue
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Interests */}
        {step === 3 && (
          <div className="space-y-8 animate-fadeIn" data-testid="onboarding-step-3">
            <div className="text-center space-y-2">
              <h1 className="text-3xl md:text-4xl font-serif text-sage-darker">
                What interests you?
              </h1>
              <p className="text-muted-foreground">
                Select topics you&apos;d like to explore. You can change these later.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              {interests.map((interest) => {
                const isSelected = formData.interests.includes(interest);
                return (
                  <button
                    key={interest}
                    onClick={() => handleInterestToggle(interest)}
                    className={`px-4 py-2 rounded-full border text-sm transition-all duration-300 ${
                      isSelected
                        ? 'border-sage bg-sage/20 text-sage-dark'
                        : 'border-sage/20 text-muted-foreground hover:border-sage/40 bg-white'
                    }`}
                    data-testid={`interest-${interest.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {interest}
                    {isSelected && <X className="w-4 h-4 ml-2 inline" />}
                  </button>
                );
              })}
            </div>

            {formData.interests.length > 0 && (
              <p className="text-sm text-muted-foreground text-center">
                {formData.interests.length} interests selected
              </p>
            )}

            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={handleBack}
                className="flex-1 border-sage/30 text-sage-darker hover:bg-sage/10 rounded-full py-6"
                data-testid="back-btn"
              >
                Back
              </Button>
              <Button
                onClick={handleNext}
                className="flex-1 bg-sage text-white hover:bg-sage-dark rounded-full py-6 font-medium"
                data-testid="next-step-btn"
              >
                Continue
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Help Topics */}
        {step === 4 && (
          <div className="space-y-8 animate-fadeIn" data-testid="onboarding-step-4">
            <div className="text-center space-y-2">
              <h1 className="text-3xl md:text-4xl font-serif text-sage-darker">
                Community Support
              </h1>
              <p className="text-muted-foreground">
                Help us connect you with the right people
              </p>
            </div>

            <div className="space-y-6">
              {/* Looking for help */}
              <div className="space-y-3">
                <label className="text-sm text-sage-darker font-medium flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-rose-500" /> 
                  I&apos;m looking for help with...
                </label>
                <div className="flex flex-wrap gap-2">
                  {HELP_TOPICS.map((topic) => {
                    const isSelected = formData.looking_for_help.includes(topic);
                    return (
                      <button
                        key={topic}
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          looking_for_help: isSelected 
                            ? prev.looking_for_help.filter(t => t !== topic)
                            : [...prev.looking_for_help, topic]
                        }))}
                        className={`px-3 py-1.5 rounded-full border text-xs transition-all ${
                          isSelected
                            ? 'border-rose-300 bg-rose-100 text-rose-700'
                            : 'border-sage/20 text-muted-foreground hover:border-rose-200 bg-white'
                        }`}
                      >
                        {topic}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Willing to help */}
              <div className="space-y-3">
                <label className="text-sm text-sage-darker font-medium flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-green-500" /> 
                  I can help others with...
                </label>
                <div className="flex flex-wrap gap-2">
                  {HELP_TOPICS.map((topic) => {
                    const isSelected = formData.willing_to_help.includes(topic);
                    return (
                      <button
                        key={topic}
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          willing_to_help: isSelected 
                            ? prev.willing_to_help.filter(t => t !== topic)
                            : [...prev.willing_to_help, topic]
                        }))}
                        className={`px-3 py-1.5 rounded-full border text-xs transition-all ${
                          isSelected
                            ? 'border-green-300 bg-green-100 text-green-700'
                            : 'border-sage/20 text-muted-foreground hover:border-green-200 bg-white'
                        }`}
                      >
                        {topic}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground text-center">
              This helps us match you with moms who can support you and those you can support
            </p>

            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={handleBack}
                className="flex-1 border-sage/30 text-sage-darker hover:bg-sage/10 rounded-full py-6"
              >
                Back
              </Button>
              <Button
                onClick={handleNext}
                className="flex-1 bg-sage text-white hover:bg-sage-dark rounded-full py-6 font-medium"
              >
                Continue
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Complete */}
        {step === 5 && (
          <div className="space-y-8 animate-fadeIn" data-testid="onboarding-step-5">
            <div className="text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-sage/20 flex items-center justify-center mx-auto">
                <Heart className="w-10 h-10 text-sage-dark" />
              </div>
              <h1 className="text-3xl md:text-4xl font-serif text-sage-darker">
                You&apos;re all set!
              </h1>
              <p className="text-muted-foreground max-w-md mx-auto">
                Your profile is ready. You can always update these details later from your profile settings.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-sage/20 space-y-4">
              <h3 className="font-medium text-sage-darker">Profile Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Phase</span>
                  <span className="text-sage-darker font-medium">
                    {phases.find(p => p.value === formData.phase)?.label || formData.phase}
                  </span>
                </div>
                {formData.location && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Location</span>
                    <span className="text-sage-darker">{formData.location}</span>
                  </div>
                )}
                {formData.working_status && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Work Status</span>
                    <span className="text-sage-darker">
                      {WORKING_STATUS_OPTIONS.find(o => o.value === formData.working_status)?.label}
                    </span>
                  </div>
                )}
                {formData.children_info && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Motherhood</span>
                    <span className="text-sage-darker">
                      {getChildrenInfoLabel(formData.children_info)}
                    </span>
                  </div>
                )}
                {formData.interests.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Interests</span>
                    <span className="text-sage-darker">{formData.interests.length} selected</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={handleBack}
                className="flex-1 border-sage/30 text-sage-darker hover:bg-sage/10 rounded-full py-6"
                data-testid="back-btn"
              >
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-sage text-white hover:bg-sage-dark rounded-full py-6 font-medium"
                data-testid="complete-profile-btn"
              >
                {loading ? "Saving..." : "Enter WOMBS"}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Onboarding;
